import React, { useState, useCallback } from "react";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import TutoringAgentFileUpload from "../components/TutoringAgentFileUpload";
import axios from "../api/Axios";

// Step 1: Tutoring Agent Details and Contact Information
const Step1 = ({
  step1Data,
  handleStep1Change,
  validateStep1,
  submitStep1Data,
  loadingStates,
  nextStep,
  validationErrors,
  setValidationErrors,
  selectedFiles,
  handleFileSelected,
  handleFileRemoved,
  documentErrors,
  agentType
}) => {
  


  // Email uniqueness check state
  const [emailChecking, setEmailChecking] = React.useState(false);
  const [emailUnique, setEmailUnique] = React.useState(true);
  const [emailFormatValid, setEmailFormatValid] = React.useState(true);
  const emailCheckTimeout = React.useRef(null);

  // Debounced email uniqueness check on change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    handleStep1Change('email_address', value);
    setEmailUnique(true); // reset uniqueness on change
    const valid = /\S+@\S+\.\S+/.test(value);
    setEmailFormatValid(valid || value === '');
    if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
    if (valid) {
      setEmailChecking(true);
      emailCheckTimeout.current = setTimeout(async () => {
        try {
          const resp = await axios.post('/api/accounts/check-email/', {
            email_address: value,
            user_type: 'tutoring_agent',
          });
          setEmailUnique(!resp.data.exists);
        } catch (e) {
          setEmailUnique(true);
        } finally {
          setEmailChecking(false);
        }
      }, 400);
    } else {
      setEmailChecking(false);
    }
  };

  const handleNext = async () => {
    const errors = validateStep1();
    if (!emailFormatValid) {
      errors.email_address = 'Please enter a valid email address';
    }
    if (!emailUnique) {
      errors.email_address = 'This email address is already in use.';
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const success = await submitStep1Data();
    if (success) {
      nextStep();
    }
  };

  return (
    <div>
      {/* Entity-specific organization details */}
      {agentType === 'entity' && (
        <>
          <h3>Organization Details</h3>
          
          {/* Organization Name */}
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.organization_name ? 'is-invalid' : ''}`}
              type="text"
              id="organizationNameInput"
              value={step1Data.organization_name}
              onChange={(e) => handleStep1Change('organization_name', e.target.value)}
              placeholder="Organization Name"
            />
            <label htmlFor="organizationNameInput">Organization/Company Name *</label>
            {validationErrors.organization_name && (
              <div className="invalid-feedback">{validationErrors.organization_name}</div>
            )}
          </div>

          {/* Registration Number and Organization Phone - Side by side */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.registration_number ? 'is-invalid' : ''}`}
                  type="text"
                  id="registrationNumberInput"
                  value={step1Data.registration_number}
                  onChange={(e) => handleStep1Change('registration_number', e.target.value)}
                  placeholder="Registration Number"
                />
                <label htmlFor="registrationNumberInput">Business Registration Number *</label>
                {validationErrors.registration_number && (
                  <div className="invalid-feedback">{validationErrors.registration_number}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-outline mb-3">
                <PhoneInput
                  className={`${validationErrors.organization_phone ? 'phone-input-invalid' : ''}`}
                  value={step1Data.organization_phone}
                  onChange={(value) => handleStep1Change('organization_phone', value || '')}
                  defaultCountry="ZA"
                  placeholder="Enter organization phone number"
                  international
                  countryCallingCodeEditable={false}
                />
                <label className="form-label">Organization Phone *</label>
                {validationErrors.organization_phone && (
                  <div className="invalid-feedback">{validationErrors.organization_phone}</div>
                )}
              </div>
            </div>
          </div>

          <h3>Representative Identification</h3>
        </>
      )}

      {/* Individual Identification or Representative Identification (for entities) */}
      {agentType === 'individual' && <h3>Identification</h3>}

      {/* First Name and Last Name */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.first_name ? 'is-invalid' : ''}`}
              type="text"
              id="firstNameInput"
              value={step1Data.first_name}
              onChange={(e) => handleStep1Change('first_name', e.target.value)}
              placeholder="First Name"
            />
            <label htmlFor="firstNameInput">First Name *</label>
            {validationErrors.first_name && (
              <div className="invalid-feedback">{validationErrors.first_name}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.last_name ? 'is-invalid' : ''}`}
              type="text"
              id="lastNameInput"
              value={step1Data.last_name}
              onChange={(e) => handleStep1Change('last_name', e.target.value)}
              placeholder="Last Name"
            />
            <label htmlFor="lastNameInput">Last Name *</label>
            {validationErrors.last_name && (
              <div className="invalid-feedback">{validationErrors.last_name}</div>
            )}
          </div>
        </div>
      </div>

      {/* Email Address */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.email_address || !emailUnique || !emailFormatValid ? 'is-invalid' : ''}`}
          type="email"
          id="emailAddressInput"
          value={step1Data.email_address}
          onChange={handleEmailChange}
          placeholder="Email Address"
        />
        <label htmlFor="emailAddressInput">
          {agentType === 'entity' ? 'Representative Email Address *' : 'Email Address *'}
        </label>
        {emailChecking && (
          <div className="form-text">Checking email availability...</div>
        )}
        {!emailFormatValid && (
          <div className="invalid-feedback">Please enter a valid email address</div>
        )}
        {(!emailUnique && emailFormatValid) && (
          <div className="invalid-feedback">This email address is already in use.</div>
        )}
        {validationErrors.email_address && (
          <div className="invalid-feedback">{validationErrors.email_address}</div>
        )}
      </div>

      {/* Citizenship and ID Document Type */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.citizenship ? 'is-invalid' : ''}`}
              id="citizenshipSelect"
              value={step1Data.citizenship}
              onChange={(e) => handleStep1Change('citizenship', e.target.value)}
            >
              <option value="">Select Citizenship</option>
              <option value="South Africa">South Africa</option>
              <option value="Other">Other</option>
            </select>
            <label htmlFor="citizenshipSelect">Citizenship *</label>
            {validationErrors.citizenship && (
              <div className="invalid-feedback">{validationErrors.citizenship}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.id_document_type ? 'is-invalid' : ''}`}
              id="idDocumentTypeSelect"
              value={step1Data.id_document_type}
              onChange={(e) => handleStep1Change('id_document_type', e.target.value)}
            >
              <option value="">Select ID Document Type</option>
              <option value="passport">Passport</option>
              <option value="national_id">National ID</option>
              <option value="drivers_license">Driver's License</option>
              <option value="refugee travel document">Refugee Travel Document</option>
            </select>
            <label htmlFor="idDocumentTypeSelect">ID Document Type *</label>
            {validationErrors.id_document_type && (
              <div className="invalid-feedback">{validationErrors.id_document_type}</div>
            )}
          </div>
        </div>
      </div>

      {/* ID Number */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.id_number ? 'is-invalid' : ''}`}
          type="text"
          id="idNumberInput"
          value={step1Data.id_number}
          onChange={(e) => handleStep1Change('id_number', e.target.value)}
          placeholder="ID Number"
        />
        <label htmlFor="idNumberInput">ID Number *</label>
        {validationErrors.id_number && (
          <div className="invalid-feedback">{validationErrors.id_number}</div>
        )}
      </div>

      {/* Phone Number */}
      <div className="form-outline mb-3">
        <PhoneInput
          className={`${validationErrors.phone_number ? 'phone-input-invalid' : ''}`}
          value={step1Data.phone_number}
          onChange={(value) => handleStep1Change('phone_number', value || '')}
          defaultCountry="ZA"
          placeholder="Enter phone number"
          international
          countryCallingCodeEditable={false}
        />
        <label className="form-label">
          {agentType === 'entity' ? 'Representative Phone Number *' : 'Phone Number *'}
        </label>
        {validationErrors.phone_number && (
          <div className="invalid-feedback">{validationErrors.phone_number}</div>
        )}
      </div>

      {/* Required Documents */}
      <h3>Required Document</h3>
      <div id="Required Documents" className="row g-3 mb-3">
        {agentType === 'individual' ? (
          <TutoringAgentFileUpload
            documentType="id_passport"
            title="Photo of Identity/Passport Document"
            onFileSelected={handleFileSelected}
            onFileRemoved={handleFileRemoved}
            selectedFile={selectedFiles.get('id_passport')}
            hasError={documentErrors.has('id_passport')}
          />
        ) : (
          <TutoringAgentFileUpload
            documentType="company_registration"
            title="Company Registration Certificate"
            onFileSelected={handleFileSelected}
            onFileRemoved={handleFileRemoved}
            selectedFile={selectedFiles.get('company_registration')}
            hasError={documentErrors.has('company_registration')}
          />
        )}
      </div>

      <button
        className="btn btn-primary mt-4"
        onClick={handleNext}
        disabled={loadingStates.step1}
      >
        {loadingStates.step1 ? 'Submitting...' : 'Continue to Address'}
      </button>

      {/* Styles */}
      <style>
        {`
          h3 {
            font-size: 22px;
            color: #495057;
            font-weight: 600;
            margin: 2rem 0 1.5rem 0;
            padding: 0.75rem 0;
            border-bottom: 2px solid #e9ecef;
            position: relative;
          }
          
          h3::before {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 60px;
            height: 2px;
            background: linear-gradient(45deg, #0d6efd, #6610f2);
            border-radius: 1px;
          }
          
          .form-control.is-invalid,
          .form-select.is-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
            background-color: rgba(220, 53, 69, 0.05);
          }
          
          .invalid-feedback {
            display: block;
            width: 100%;
            margin-top: 0.5rem;
            font-size: 0.875rem;
            color: #fff;
            text-align: left;
            position: relative;
            z-index: 1;
            padding: 0.5rem 0.75rem;
            background: #dc3545;
            border-radius: 0.375rem;
            border: 1px solid #dc3545;
          }
          
          .PhoneInput {
            position: relative;
            display: flex;
            align-items: center;
            border: 1px solid #ced4da;
            border-radius: 4px;
            background: transparent;
            padding: 0;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            min-height: 58px;
            height: 58px;
          }
          
          .PhoneInput:focus-within {
            border-color: #0d6efd;
            box-shadow: none;
          }
          
          .PhoneInput.phone-input-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .PhoneInputInput {
            border: none;
            background: transparent;
            padding: 16px 12px 16px 8px;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            outline: none;
            flex: 1;
            height: 100%;
          }
        `}
      </style>
    </div>
  );
};

// Step 2: Address Information
const Step2 = ({
  step2Data,
  handleStep2Change,
  validateStep2,
  submitStep2Data,
  loadingStates,
  previousStep,
  nextStep,
  validationErrors,
  setValidationErrors,
  agentType
}) => {

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateStep2();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    const success = await submitStep2Data();
    if (success) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    previousStep();
  };

  return (
    <div>
      {/* Entity agents need organization address section */}
      {agentType === 'entity' && (
        <>
          <h3>Organization Address</h3>
          
          <div className="form-floating mb-3">
            <input
              className="form-control"
              type="text"
              id="orgCountryInput"
              value="South Africa"
              readOnly
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
            />
            <label htmlFor="orgCountryInput">Country</label>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_address_line_1 ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgAddressLine1Input"
                  value={step2Data.org_address_line_1}
                  onChange={(e) => handleStep2Change('org_address_line_1', e.target.value)}
                  placeholder="Address Line 1"
                />
                <label htmlFor="orgAddressLine1Input">Address Line 1 *</label>
                {validationErrors.org_address_line_1 && (
                  <div className="invalid-feedback">{validationErrors.org_address_line_1}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className="form-control"
                  type="text"
                  id="orgAddressLine2Input"
                  value={step2Data.org_address_line_2}
                  onChange={(e) => handleStep2Change('org_address_line_2', e.target.value)}
                  placeholder="Address Line 2"
                />
                <label htmlFor="orgAddressLine2Input">Address Line 2</label>
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_suburb ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgSuburbInput"
                  value={step2Data.org_suburb}
                  onChange={(e) => handleStep2Change('org_suburb', e.target.value)}
                  placeholder="Suburb"
                />
                <label htmlFor="orgSuburbInput">Suburb *</label>
                {validationErrors.org_suburb && (
                  <div className="invalid-feedback">{validationErrors.org_suburb}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_city ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgCityInput"
                  value={step2Data.org_city}
                  onChange={(e) => handleStep2Change('org_city', e.target.value)}
                  placeholder="City"
                />
                <label htmlFor="orgCityInput">City *</label>
                {validationErrors.org_city && (
                  <div className="invalid-feedback">{validationErrors.org_city}</div>
                )}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_postcode ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgPostcodeInput"
                  value={step2Data.org_postcode}
                  onChange={(e) => handleStep2Change('org_postcode', e.target.value)}
                  placeholder="Postcode"
                />
                <label htmlFor="orgPostcodeInput">Postcode *</label>
                {validationErrors.org_postcode && (
                  <div className="invalid-feedback">{validationErrors.org_postcode}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <select
                  className={`form-select ${validationErrors.org_province ? 'is-invalid' : ''}`}
                  id="orgProvinceSelect"
                  value={step2Data.org_province}
                  onChange={(e) => handleStep2Change('org_province', e.target.value)}
                >
                  <option value="">Select Province</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Free State">Free State</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="Northern Cape">Northern Cape</option>
                  <option value="North West">North West</option>
                  <option value="Western Cape">Western Cape</option>
                </select>
                <label htmlFor="orgProvinceSelect">Province *</label>
                {validationErrors.org_province && (
                  <div className="invalid-feedback">{validationErrors.org_province}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Individual/Representative Address */}
      <h3>{agentType === 'entity' ? 'Representative Address' : 'Current Address'}</h3>
      
      <div className="form-floating mb-3">
        <input
          className="form-control"
          type="text"
          id="countryInput"
          value="South Africa"
          readOnly
          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
        />
        <label htmlFor="countryInput">Country</label>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.address_line_1 ? 'is-invalid' : ''}`}
              type="text"
              id="addressLine1Input"
              value={step2Data.address_line_1}
              onChange={(e) => handleStep2Change('address_line_1', e.target.value)}
              placeholder="Address Line 1"
            />
            <label htmlFor="addressLine1Input">Address Line 1 *</label>
            {validationErrors.address_line_1 && (
              <div className="invalid-feedback">{validationErrors.address_line_1}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className="form-control"
              type="text"
              id="addressLine2Input"
              value={step2Data.address_line_2}
              onChange={(e) => handleStep2Change('address_line_2', e.target.value)}
              placeholder="Address Line 2"
            />
            <label htmlFor="addressLine2Input">Address Line 2</label>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.suburb ? 'is-invalid' : ''}`}
              type="text"
              id="suburbInput"
              value={step2Data.suburb}
              onChange={(e) => handleStep2Change('suburb', e.target.value)}
              placeholder="Suburb"
            />
            <label htmlFor="suburbInput">Suburb *</label>
            {validationErrors.suburb && (
              <div className="invalid-feedback">{validationErrors.suburb}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
              type="text"
              id="cityInput"
              value={step2Data.city}
              onChange={(e) => handleStep2Change('city', e.target.value)}
              placeholder="City"
            />
            <label htmlFor="cityInput">City *</label>
            {validationErrors.city && (
              <div className="invalid-feedback">{validationErrors.city}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.postcode ? 'is-invalid' : ''}`}
              type="text"
              id="postcodeInput"
              value={step2Data.postcode}
              onChange={(e) => handleStep2Change('postcode', e.target.value)}
              placeholder="Postcode"
            />
            <label htmlFor="postcodeInput">Postcode *</label>
            {validationErrors.postcode && (
              <div className="invalid-feedback">{validationErrors.postcode}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.province ? 'is-invalid' : ''}`}
              id="provinceSelect"
              value={step2Data.province}
              onChange={(e) => handleStep2Change('province', e.target.value)}
            >
              <option value="">Select Province</option>
              <option value="Eastern Cape">Eastern Cape</option>
              <option value="Free State">Free State</option>
              <option value="Gauteng">Gauteng</option>
              <option value="KwaZulu-Natal">KwaZulu-Natal</option>
              <option value="Limpopo">Limpopo</option>
              <option value="Mpumalanga">Mpumalanga</option>
              <option value="Northern Cape">Northern Cape</option>
              <option value="North West">North West</option>
              <option value="Western Cape">Western Cape</option>
            </select>
            <label htmlFor="provinceSelect">Province *</label>
            {validationErrors.province && (
              <div className="invalid-feedback">{validationErrors.province}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevious}
          >
            Previous
          </button>
        </div>
        <div className="col-md-6 text-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loadingStates.step2}
          >
            {loadingStates.step2 ? 'Submitting...' : 'Continue to Bank Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Step 3: Bank Details
const Step3 = ({
  step3Data,
  handleStep3Change,
  validateStep3,
  submitStep3Data,
  loadingStates,
  previousStep,
  nextStep,
  validationErrors,
  setValidationErrors,
  selectedFiles,
  handleFileSelected,
  handleFileRemoved,
  documentErrors
}) => {
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateStep3();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    const success = await submitStep3Data();
    if (success) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    previousStep();
  };

  return (
    <div>
      <h3>Bank Details</h3>
      
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.bank_name ? 'is-invalid' : ''}`}
          type="text"
          id="bankNameInput"
          value={step3Data.bank_name}
          onChange={(e) => handleStep3Change('bank_name', e.target.value)}
          placeholder="Bank Name"
        />
        <label htmlFor="bankNameInput">Bank Name *</label>
        {validationErrors.bank_name && (
          <div className="invalid-feedback">{validationErrors.bank_name}</div>
        )}
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.account_number ? 'is-invalid' : ''}`}
              type="text"
              id="accountNumberInput"
              value={step3Data.account_number}
              onChange={(e) => handleStep3Change('account_number', e.target.value)}
              placeholder="Account Number"
            />
            <label htmlFor="accountNumberInput">Account Number *</label>
            {validationErrors.account_number && (
              <div className="invalid-feedback">{validationErrors.account_number}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.account_type ? 'is-invalid' : ''}`}
              id="accountTypeSelect"
              value={step3Data.account_type}
              onChange={(e) => handleStep3Change('account_type', e.target.value)}
            >
              <option value="">Select Account Type</option>
              <option value="current">Current Account</option>
              <option value="savings">Savings Account</option>
              <option value="business">Business Account</option>
              <option value="transmission">Transmission Account</option>
            </select>
            <label htmlFor="accountTypeSelect">Account Type *</label>
            {validationErrors.account_type && (
              <div className="invalid-feedback">{validationErrors.account_type}</div>
            )}
          </div>
        </div>
      </div>

      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.branch_code ? 'is-invalid' : ''}`}
          type="text"
          id="branchCodeInput"
          value={step3Data.branch_code}
          onChange={(e) => handleStep3Change('branch_code', e.target.value)}
          placeholder="Branch Code"
        />
        <label htmlFor="branchCodeInput">Branch/Sort Code *</label>
        {validationErrors.branch_code && (
          <div className="invalid-feedback">{validationErrors.branch_code}</div>
        )}
      </div>

      <div className="mb-4">
        <label className="form-label">Proof of Account *</label>
        <TutoringAgentFileUpload
          documentType="proof_of_account"
          title="Proof of Bank Account"
          onFileSelected={handleFileSelected}
          onFileRemoved={handleFileRemoved}
          selectedFile={selectedFiles.get('proof_of_account')}
          hasError={documentErrors.has('proof_of_account')}
        />
        {validationErrors.proof_of_account && (
          <div className="text-danger mt-2">{validationErrors.proof_of_account}</div>
        )}
      </div>

      <div className="row mt-4">
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevious}
          >
            Previous
          </button>
        </div>
        <div className="col-md-6 text-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loadingStates.step3}
          >
            {loadingStates.step3 ? 'Creating Agent...' : 'Complete Registration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Completion Success Component
const CompletionSuccess = ({ onComplete }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '3rem 1rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <i className="bi bi-check-circle-fill" style={{ fontSize: '4rem', color: '#198754' }}></i>
      </div>
      <div style={{ 
        backgroundColor: '#d1e7dd',
        border: '1px solid #badbcc',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 0.5rem 1rem rgba(25, 135, 84, 0.15)'
      }}>
        <h4 style={{ color: '#0f5132', marginBottom: '1rem', fontWeight: 'bold' }}>
          <i className="bi bi-check-circle" style={{ marginRight: '0.5rem' }}></i>
          Tutoring Agent Created Successfully!
        </h4>
        <p style={{ color: '#0f5132', fontSize: '1.1rem', margin: '0' }}>
          The tutoring agent has been created. They will receive an email to set their password and activate their account.
        </p>
      </div>
      <div style={{ color: '#6c757d' }}>
        <p style={{ margin: '0' }}>Redirecting to type selection...</p>
      </div>
    </div>
  );
};

// Main Tutoring Agent Form Wizard Component
const TutoringAgentFormWizard = ({ agentType, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardCompleted, setWizardCompleted] = useState(false);

  const stepLabels = [
    { label: "Identification", icon: "bi-person-badge" },
    { label: "Address", icon: "bi-geo-alt" },
    { label: "Bank Details", icon: "bi-bank" }
  ];

  const [step1Data, setStep1Data] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    citizenship: '',
    id_document_type: '',
    id_number: '',
    phone_number: '',
    organization_phone: '',
    registration_number: '',
    organization_name: ''
  });

  const [step2Data, setStep2Data] = useState({
    country: 'South Africa',
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    postcode: '',
    province: '',
    org_country: 'South Africa',
    org_address_line_1: '',
    org_address_line_2: '',
    org_suburb: '',
    org_city: '',
    org_postcode: '',
    org_province: ''
  });

  const [step3Data, setStep3Data] = useState({
    bank_name: '',
    account_number: '',
    account_type: '',
    proof_of_account: null
  });
  
  const [loadingStates, setLoadingStates] = useState({ 
    step1: false,
    step2: false,
    step3: false
  });

  const [validationErrors, setValidationErrors] = useState({});
  
  // File upload state - stores actual File objects
  const [selectedFiles, setSelectedFiles] = useState(new Map());
  const [documentErrors, setDocumentErrors] = useState(new Set());

  const handleFileSelected = useCallback((docType, file) => {
    setSelectedFiles(prev => {
      const newMap = new Map(prev);
      newMap.set(docType, file);
      return newMap;
    });
    
    // Also update step3Data for proof_of_account
    if (docType === 'proof_of_account') {
      setStep3Data(prev => ({ ...prev, proof_of_account: file }));
    }
    
    setDocumentErrors(prev => {
      const newSet = new Set(prev);
      newSet.delete(docType);
      return newSet;
    });
  }, []);

  const handleFileRemoved = useCallback((docType) => {
    setSelectedFiles(prev => {
      const newMap = new Map(prev);
      newMap.delete(docType);
      return newMap;
    });
    
    if (docType === 'proof_of_account') {
      setStep3Data(prev => ({ ...prev, proof_of_account: null }));
    }
    
    setDocumentErrors(prev => new Set([...prev, docType]));
  }, []);

  const handleStep1Change = (field, value) => {
    setStep1Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep2Change = (field, value) => {
    setStep2Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep3Change = (field, value) => {
    setStep3Data(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    const errors = {};
    
    if (!step1Data.first_name) errors.first_name = 'First name is required';
    if (!step1Data.last_name) errors.last_name = 'Last name is required';
    if (!step1Data.email_address) {
      errors.email_address = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(step1Data.email_address)) {
      errors.email_address = 'Please enter a valid email address';
    }
    if (!step1Data.citizenship) errors.citizenship = 'Citizenship is required';
    if (!step1Data.id_document_type) errors.id_document_type = 'ID document type is required';
    if (!step1Data.id_number) errors.id_number = 'ID number is required';
    if (!step1Data.phone_number) {
      errors.phone_number = 'Phone number is required';
    } else if (step1Data.phone_number.length < 8) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    
    if (agentType === 'entity') {
      if (!step1Data.organization_name) errors.organization_name = 'Organization name is required';
      if (!step1Data.registration_number) errors.registration_number = 'Business registration number is required';
      if (!step1Data.organization_phone) {
        errors.organization_phone = 'Organization phone number is required';
      } else if (step1Data.organization_phone.length < 8) {
        errors.organization_phone = 'Please enter a valid organization phone number';
      }
    }
    
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    
    if (!step2Data.address_line_1) errors.address_line_1 = 'Address line 1 is required';
    if (!step2Data.suburb) errors.suburb = 'Suburb is required';
    if (!step2Data.city) errors.city = 'City is required';
    if (!step2Data.postcode) errors.postcode = 'Postcode is required';
    if (!step2Data.province) errors.province = 'Province is required';
    
    if (agentType === 'entity') {
      if (!step2Data.org_address_line_1) errors.org_address_line_1 = 'Organization address line 1 is required';
      if (!step2Data.org_suburb) errors.org_suburb = 'Organization suburb is required';
      if (!step2Data.org_city) errors.org_city = 'Organization city is required';
      if (!step2Data.org_postcode) errors.org_postcode = 'Organization postcode is required';
      if (!step2Data.org_province) errors.org_province = 'Organization province is required';
    }
    
    return errors;
  };

  const validateStep3 = () => {
    const errors = {};
    
    if (!step3Data.bank_name) errors.bank_name = 'Bank name is required';
    if (!step3Data.account_number) errors.account_number = 'Account number is required';
    if (!step3Data.account_type) errors.account_type = 'Account type is required';
    if (!selectedFiles.has('proof_of_account')) errors.proof_of_account = 'Proof of account is required';
    
    return errors;
  };

  const submitStep1Data = async () => {
    setLoadingStates(prev => ({ ...prev, step1: true }));
    
    try {
      // Step 1 doesn't actually submit to backend - just validation
      return true;
    } catch (error) {
      console.error('Error in step 1:', error);
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step1: false }));
    }
  };

  const submitStep2Data = async () => {
    setLoadingStates(prev => ({ ...prev, step2: true }));
    
    try {
      // Step 2 doesn't actually submit to backend - just validation
      return true;
    } catch (error) {
      console.error('Error in step 2:', error);
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step2: false }));
    }
  };

  const submitStep3Data = async () => {
    setLoadingStates(prev => ({ ...prev, step3: true }));
    
    try {
      const formData = new FormData();
      
      // Step 1 data
      formData.append('agent_type', agentType);
      formData.append('first_name', step1Data.first_name);
      formData.append('last_name', step1Data.last_name);
      formData.append('email_address', step1Data.email_address);
      formData.append('citizenship', step1Data.citizenship);
      formData.append('id_document_type', step1Data.id_document_type);
      formData.append('id_number', step1Data.id_number);
      formData.append('phone_number', step1Data.phone_number);
      
      if (agentType === 'entity') {
        formData.append('organization_name', step1Data.organization_name);
        formData.append('registration_number', step1Data.registration_number);
        formData.append('organization_phone', step1Data.organization_phone);
      }
      
      // Step 2 data
      const personal_address = `${step2Data.address_line_1}${step2Data.address_line_2 ? ', ' + step2Data.address_line_2 : ''}, ${step2Data.suburb}, ${step2Data.city}, ${step2Data.province}, ${step2Data.postcode}, South Africa`;
      formData.append('personal_address', personal_address);
      
      if (agentType === 'entity') {
        const business_address = `${step2Data.org_address_line_1}${step2Data.org_address_line_2 ? ', ' + step2Data.org_address_line_2 : ''}, ${step2Data.org_suburb}, ${step2Data.org_city}, ${step2Data.org_province}, ${step2Data.org_postcode}, South Africa`;
        formData.append('business_address', business_address);
      }
      
      // Step 3 data
      formData.append('bank_name', step3Data.bank_name);
      formData.append('account_number', step3Data.account_number);
      formData.append('account_type', step3Data.account_type);
      formData.append('branch_code', step3Data.branch_code);
      
      // Append files
      const proofOfAccount = selectedFiles.get('proof_of_account');
      if (proofOfAccount) {
        formData.append('proof_of_account', proofOfAccount);
      }
      
      const idDoc = agentType === 'individual' 
        ? selectedFiles.get('id_passport')
        : selectedFiles.get('company_registration');
      if (idDoc) {
        const fieldName = agentType === 'individual' ? 'id_passport' : 'company_registration';
        formData.append(fieldName, idDoc);
      }
      
      await axios.post('api/accounts/create-tutoring-agent/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return true;
    } catch (error) {
      console.error('Error creating tutoring agent:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          setValidationErrors(errorData);
        }
      }
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step3: false }));
    }
  };

  const nextStep = () => {
    if (currentStep < stepLabels.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      setWizardCompleted(true);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (wizardCompleted) {
    return <CompletionSuccess onComplete={onComplete} />;
  }

  return (
    <>
      <div className="wizardbox">
        <div className="step-indicator mb-4 d-flex justify-content-between align-items-end" style={{ gap: 0 }}>
          {stepLabels.map((step, index) => {
            const stepNum = index + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            
            return (
              <div
                key={stepNum}
                className={`step text-center ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                style={{
                  flex: 1,
                  position: "relative",
                  zIndex: 1,
                  minWidth: 100,
                  cursor: 'default'
                }}
              >
                <div
                  className={`circle d-flex align-items-center justify-content-center mb-2 ${isActive ? "circle-active" : ""} ${isCompleted ? "circle-completed" : ""}`}
                  style={{
                    width: 60,
                    height: 60,
                    margin: "0 auto",
                    borderRadius: "50%",
                    background: isActive
                      ? "linear-gradient(135deg, #296f93 60%, #5bc0de 100%)"
                      : isCompleted
                      ? "linear-gradient(135deg, #28a745 60%, #20c997 100%)"
                      : "#e9ecef",
                    color: isActive || isCompleted ? "#fff" : "#adb5bd",
                    fontSize: 32,
                    boxShadow: isActive
                      ? "0 0 0 4px #296f9333"
                      : isCompleted
                      ? "0 0 0 2px #28a74533"
                      : "none",
                    transition: "all 0.3s ease"
                  }}
                >
                  {isCompleted ? (
                    <i className="bi bi-check-lg"></i>
                  ) : (
                    <i className={step.icon}></i>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: isActive || isCompleted ? 600 : 400,
                    color: isActive ? "#296f93" : isCompleted ? "#28a745" : "#6c757d",
                    transition: "all 0.3s ease"
                  }}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>

        {currentStep === 1 && (
          <Step1
            step1Data={step1Data}
            handleStep1Change={handleStep1Change}
            validateStep1={validateStep1}
            submitStep1Data={submitStep1Data}
            loadingStates={loadingStates}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            selectedFiles={selectedFiles}
            handleFileSelected={handleFileSelected}
            handleFileRemoved={handleFileRemoved}
            documentErrors={documentErrors}
            agentType={agentType}
          />
        )}

        {currentStep === 2 && (
          <Step2
            step2Data={step2Data}
            handleStep2Change={handleStep2Change}
            validateStep2={validateStep2}
            submitStep2Data={submitStep2Data}
            loadingStates={loadingStates}
            previousStep={previousStep}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            agentType={agentType}
          />
        )}

        {currentStep === 3 && (
          <Step3
            step3Data={step3Data}
            handleStep3Change={handleStep3Change}
            validateStep3={validateStep3}
            submitStep3Data={submitStep3Data}
            loadingStates={loadingStates}
            previousStep={previousStep}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            selectedFiles={selectedFiles}
            handleFileSelected={handleFileSelected}
            handleFileRemoved={handleFileRemoved}
            documentErrors={documentErrors}
          />
        )}
      </div>
    </>
  );
};

export default TutoringAgentFormWizard;
